import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Thread } from "@/features/chat/types/sdk";
import { Plus } from "lucide-react";
import { NewConversationModal, type ProjectFormData } from "./NewConversationModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface ConversationSelectionModalProps {
  threads: Thread[];
  onSelect: (conversationId: string) => void;
  onCreateNew: (data: ProjectFormData) => Promise<void>;
}

/**
 * Modal for selecting or creating a conversation.
 * Shown when user logs in without an active conversation.
 */
export function ConversationSelectionModal({
  threads,
  onSelect,
  onCreateNew,
}: ConversationSelectionModalProps) {
  const { t } = useTranslation("common");
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);

  return (
    <>
      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onCreateProject={onCreateNew}
      />

      {/* Conversation Selection Modal - hide when NewConversationModal is open */}
      {!showNewConversationModal && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background md:items-center md:justify-center md:bg-utrecht-gray/90 md:backdrop-blur-sm">
          <div className="flex flex-1 flex-col bg-background md:flex-initial md:w-full md:max-w-md md:overflow-hidden">
          {/* Header */}
          <div className="border-b border-border bg-utrecht-blue p-4">
            <h2 className="text-xl font-semibold text-white">{t("project.select")}</h2>
            <p className="text-sm text-white/90 mt-1">
              {t("project.selectDescription")}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-6">
              {/* Existing projects dropdown */}
              <div className="space-y-3">
                <Label htmlFor="conversation-select">{t("project.existing")}</Label>
                <Select onValueChange={(value) => {
                  if (value && value !== "__empty__") {
                    onSelect(value);
                  }
                }}>
                  <SelectTrigger id="conversation-select" className="w-full h-16 text-lg">
                    <SelectValue placeholder={t("project.choose")} />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="max-h-[40vh]">
                    {threads.length === 0 ? (
                      <SelectItem value="__empty__" disabled>
                        {t("project.noExisting")}
                      </SelectItem>
                    ) : (
                      threads.map((thread) => (
                        <SelectItem key={thread.thread_id} value={thread.thread_id} className="h-12 text-base">
                          {thread.metadata?.project_name || t("project.unnamed")}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Divider */}
              {threads.length > 0 && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-background px-2 text-muted-foreground">{t("common.or")}</span>
                  </div>
                </div>
              )}

              {/* Create new conversation */}
              <div className="space-y-3">
                <Label>{t("project.new")}</Label>
                <Button
                  onClick={() => setShowNewConversationModal(true)}
                  className="w-full"
                  variant="brand"
                  size="xxl"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  {t("project.createNew")}
                </Button>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}
    </>
  );
}
